import { observer } from 'mobx-react-lite'
import { useDocumentTitle } from '@/hooks/document-title'
import { useActivation } from '@/hooks/view-model-hooks'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { type BundleManagerViewModel } from '@/features/admin/bundle/BundleManagerViewModel'
import { BundleManagerPage } from '@/features/admin/bundle/BundleManager/BundleManagerPage'
import { BundleManagerContentContainer } from '@/features/admin/bundle/components/BundleManagerContent/BundleManagerContentContainer'
import { BundleDialogContainer } from '@/features/admin/bundle/BundleDialog/BundleDialogContainer'
import { ArchiveBundleDialogContainer } from '@/features/admin/bundle/ArchiveBundleDialog/ArchiveBundleDialogContainer'
import { UnarchiveBundleDialogContainer } from '@/features/admin/bundle/UnarchiveBundleDialog/UnarchiveBundleDialogContainer'
import { BulkDuplicateBundleDialogContainer } from '@/features/admin/bundle/BulkDuplicateBundleDialog/BulkDuplicateBundleDialogContainer'
import { BulkArchiveBundleDialogContainer } from '@/features/admin/bundle/BulkArchiveBundleDialog/BulkArchiveBundleDialogContainer'
import { BulkApplyDiscountDialogContainer } from '@/features/admin/bundle/BulkApplyDiscountDialog/BulkApplyDiscountDialogContainer'
import { BulkEditPriceDialogContainer } from '@/features/admin/bundle/BulkEditPriceDialog/BulkEditPriceDialogContainer'
import { WorkspaceSelectorViewHeadingContainer } from '@/features/iam/workspaces/components/WorkspaceSelectorViewHeading/WorkspaceSelectorViewHeadingContainer'

export interface BundleManagerPageContainerProps {
  vm: BundleManagerViewModel
}

export const BundleManagerPageContainer = observer(
  function BundleManagerPageContainer({ vm }: BundleManagerPageContainerProps) {
    useDocumentTitle('Bundle manager')
    useActivation(vm)

    return vm.fetch.match({
      pending: () => <ContentLoading />,
      rejected: (err: unknown) => <ContentError error={err} />,
      resolved: () => (
        <BundleManagerPage
          heading={
            <WorkspaceSelectorViewHeadingContainer
              primaryText={'Bundle manager'}
              allOptionDropdownItem={
                vm.canManageBundles ? <>All workspaces</> : null
              }
              alwaysUseDropdown={vm.workspacesUseDropdown}
              workspaces={vm.workspaces}
              dropdownTitle="Select workspace"
              onChange={vm.selectWorkspaceForFiltering}
              searcher={vm.canSearch ? vm.workspaceSearcher : undefined}
              renderName={(workspace) => {
                return <>{workspace.name}</>
              }}
              allOptionText={vm.canManageBundles ? 'All workspaces' : null}
              value={vm.selectedWorkspace}
            />
          }
          currentArchivedStatusTab={vm.currentArchivedStatusTab}
          onArchivedStatusTabChange={vm.setCurrentArchivedStatusTab}
          onAddBundle={vm.createBundleVm.open}
          isDone={vm.searcher.scrollProvider.isDone}
          hasBundles={vm.hasBundles}
          currentWorkspaceName={vm.currentWorkspaceName}
          content={<BundleManagerContentContainer vm={vm} />}
          loader={
            <InfiniteScrollLoaderContainer
              scrollProvider={vm.searcher.scrollProvider}
            />
          }
        >
          <BundleDialogContainer vm={vm.createBundleVm} />
          <BundleDialogContainer vm={vm.editBundleVm} />
          <ArchiveBundleDialogContainer vm={vm.archiveBundleVm} />
          <UnarchiveBundleDialogContainer vm={vm.unarchiveBundleVm} />
          <BundleDialogContainer vm={vm.duplicateBundleVm} />
          <BulkDuplicateBundleDialogContainer vm={vm.bulkDuplicateBundleVm} />
          <BulkArchiveBundleDialogContainer vm={vm.bulkArchiveBundleVm} />
          <BulkEditPriceDialogContainer vm={vm.bulkEditPriceVm} />
          <BulkApplyDiscountDialogContainer vm={vm.bulkApplyDiscountVm} />
        </BundleManagerPage>
      ),
    })
  },
)
